import React, { useRef, useState, useEffect, useImperativeHandle } from "react";
import InputText from "../UI/InputText";
import InputNumber from "../UI/InputNumber";
import InputRadio from "../UI/InputRadio";
import InputDate from "../UI/InputDate";
import TextArea from "../UI/InputTextArea";
import LinkTextArea from "../UI/LinkTextArea";
import DynamicTextType from "../UI/DynamicTextType/DynamicTextType";
import ArrayFieldType from "../UI/ArrayFieldType/ArrayFieldType";
import GradientColorPicker from "../UI/ColorPicker/ColorPicker";
import caretDown from '../../assets/caret-down.svg';
import Select from "../UI/Select";
import InlineTooltip from "../UI/Tooltip/InlineTooltip";
import UploadFieldType from "../UI/UploadFieldType/UploadFieldType";

const Accordion = ({ configElements, fieldValues, assetUrls, handleInputChanges, dynamicFields, handleChangeDynamicField, openIndices, setOpenIndices, expandAll, setExpandAll, accordionRef, hasGroups, setHasGroups, onFileSelected }) => {
    const fieldRefs = useRef({});
    
    setHasGroups(configElements.some(element => element.group));

    // Group elements by group_id only if group property exists
    const groupedData = {};
    configElements.forEach(element => {
        if (element.group) {
            const { group_id, group_title, order } = element.group;
            if (!groupedData[group_id]) {
                groupedData[group_id] = { group_title, order: parseInt(order), items: [] };
            }
            groupedData[group_id].items.push(element);
        }
    });
    
    // Sort groups by order
    const sortedGroups = Object.values(groupedData).sort((a, b) => a.order - b.order);

    useImperativeHandle(accordionRef, () => ({
        toggleExpandAll() {
            if (expandAll) {
              setOpenIndices([]);
            } else {
              setOpenIndices(sortedGroups.map((_, index) => index));
            }
            setExpandAll(!expandAll);
        }
    }));

    const scrollToField = (index) => {
        const element = configElements[index];
        if (!element) return;
    
        if (hasGroups || element?.group?.group_id) {
            const groupIndex = sortedGroups.findIndex(group => 
                group.items.some(item => item.id === element.id)
            );

            if (groupIndex !== -1 && !openIndices.includes(groupIndex)) {
                setOpenIndices(prev => [...prev, groupIndex]); // Expand the group
                
                setTimeout(() => {
                    scrollToElement(element.id); // Scroll after expansion
                }, 300); // Delay to allow expansion animation
                return;
            }
        } else {
          scrollToElement(element.id);
        }
    };
    
    const scrollToElement = (fieldId) => {
        const fieldElement = fieldRefs.current[fieldId];
        if (fieldElement) {
            fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    
            const input = fieldElement.querySelector('input, textarea');
            if (input) input.focus();
        }
    };    

    useEffect(() => {
        const iframeHandler = (event) => {
            var idx = configElements.findIndex(el => el.id === event.data);

            if (idx !== -1 && event.data) {
                scrollToField(idx);
            }
        };
        window.addEventListener('message', iframeHandler);
        return () => {
            window.removeEventListener("message", iframeHandler);
        };
    }, [configElements]);

    const getElementType = (element, index) => {
      return <div key={element.id}>
        {(() => {
          if (element.input.input_type === "color-picker") {
            return (
              <div key={index} ref={(el) => (fieldRefs.current[element.id] = el)}>
                <GradientColorPicker
                  label={element.input.input_label}
                  helpText={element?.input?.input_helptext}
                  value={fieldValues[element.id]}
                  onChange={(color) => handleInputChanges({ target: { name: element.id, value: color } })}
                  id={element.id}
                  error={element.error}
                  mandatory={element.rules.is_mandatory}
                />
              </div>
            );
          }
          if ((element.input.input_type == "text" || element.input.input_type == "selector" )) {
            return (
              <div key={index} ref={(el) => (fieldRefs.current[element.id] = el)}>
                <InputText
                  label={element.input.input_label}
                  value={fieldValues[element.id]}
                  handleChange={handleInputChanges}
                  id={element.id}
                  name={element.id}
                  helpText={element?.input?.input_helptext}
                  error={element.error}
                  mandatory={element.rules.is_mandatory}
                />
              </div>
            );
          } else if (element.input.input_type == "number") {
            return (
              <div key={index} ref={(el) => (fieldRefs.current[element.id] = el)}>
                <InputNumber
                  label={element.input.input_label}
                  value={fieldValues[element.id]}
                  handleChange={handleInputChanges}
                  id={element.id}
                  name={element.id}
                  helpText={element?.input?.input_helptext}
                  error={element.error}
                  mandatory={element.rules.is_mandatory}
                />
              </div>
            );
          } else if (element.input.input_type == "radio") {
            return (
              <div key={index} ref={(el) => (fieldRefs.current[element.id] = el)}>
                <InputRadio
                  label={element.input.input_label}
                  value={fieldValues[element.id]}
                  handleChange={handleInputChanges}
                  id={element.id}
                  name={element.id}
                  helpText={element?.input?.input_helptext}
                  error={element.error}
                  mandatory={element.rules.is_mandatory}
                />
              </div>
            );
          } else if (element.input.input_type == "date") {
            return (
              <div key={index} ref={(el) => (fieldRefs.current[element.id] = el)}>
                <InputDate
                  label={element.input.input_label}
                  value={fieldValues[element.id]}
                  handleChange={handleInputChanges}
                  id={element.id}
                  name={element.id}
                  helpText={element?.input?.input_helptext}
                  error={element.error}
                  mandatory={element.rules.is_mandatory}
                />
              </div>
            );
          } else if (element.input.input_type == "rich-textarea") {
            return (
              <div key={index} ref={(el) => (fieldRefs.current[element.id] = el)}>
                <TextArea
                  label={element.input.input_label}
                  value={fieldValues[element.id]?.replaceAll('&lt;', '<')?.replaceAll('&gt;', '>')}
                  handleChange={(e) => handleInputChanges({ target: { name: e.target.name, value: e.target.value.replaceAll(`"`, `'`) } })}
                  id={element.id}
                  name={element.id}
                  helpText={element?.input?.input_helptext}
                  error={element.error}
                  mandatory={element.rules.is_mandatory}
                />
              </div>
            );
          } else if (element.input.input_type == "url-textarea") {
            return (
              <div key={index} ref={(el) => (fieldRefs.current[element.id] = el)}>
                <LinkTextArea
                  label={element.input.input_label}
                  value={fieldValues[element.id]}
                  handleChange={handleInputChanges}
                  id={element.id}
                  name={element.id}
                  helpText={element?.input?.input_helptext}
                  error={element.error}
                  mandatory={element.rules.is_mandatory}
                />
              </div>
            );
          } else if (element.input.input_type == "dynamic-text") {
            return (
              <div key={index} ref={(el) => (fieldRefs.current[element.id] = el)}>
                <DynamicTextType
                  element={element}
                  label={element.input.input_label}
                  value={dynamicFields[element.id]}
                  handleChange={handleChangeDynamicField}
                  id={element.id}
                  name={element.id}
                  helpText={element?.input?.input_helptext}
                  error={element.error}
                  mandatory={element.rules.is_mandatory}
                />
              </div>
            );
        } else if (element.input.input_type === "static-options") {
          // Assuming element.input.input_default_value is an array of strings for static-options
          const dropdownOptions = Array.isArray(element?.input?.input_options)
            ? element.input.input_options.map((opt) => ({ value: opt, label: opt }))
            : []

          return (
            <div key={element.id}>
              <Select
                label={element.input.input_label}
                value={fieldValues[element.id]}
                options={dropdownOptions}
                handleChange={handleInputChanges}
                id={element.id}
                name={element.id}
                error={element.error}
                mandatory={element.rules.is_mandatory}
              />
            </div>
          )
        } else if (element.input.input_type == "array") {
            return (
              <div key={index} ref={(el) => (fieldRefs.current[element.id] = el)}>
                <ArrayFieldType
                  element={element}
                  label={element.input.input_label}
                  value={fieldValues[element.id]}
                  handleChange={handleInputChanges}
                  id={element.id}
                  helpText={element?.input?.input_helptext}
                  mandatory={element.rules.is_mandatory}
                />
              </div>
            );
          } else if (element.input.input_type === "upload") {
            // Use exp_json_field as the canonical key (matches fieldValues & resolveUploadFields)
            const uploadFieldId = element.exp_json_field || element.id;
            const rawVal = fieldValues[uploadFieldId];
            const isBlob = typeof rawVal === 'string' && rawVal.startsWith('blob:');
            const displayVal = isBlob ? rawVal : (assetUrls?.[uploadFieldId] || (typeof rawVal === 'string' ? rawVal : ''));
            
            return (
              <div key={uploadFieldId} ref={(el) => (fieldRefs.current[uploadFieldId] = el)}>
                <UploadFieldType
                  label={element.input.input_label}
                  helpText={element?.input?.input_helptext}
                  value={displayVal}
                  onChange={handleInputChanges}
                  onFileSelected={onFileSelected}
                  id={uploadFieldId}
                  name={uploadFieldId}
                  error={element.error}
                  mandatory={element.rules.is_mandatory}
                  maxSize={element.input.max_media_size || ''}
                  acceptedTypes={Array.isArray(element.input.accepted_media_types) ? element.input.accepted_media_types : []}
                />
              </div>
            );
          }
        })()}
      </div>
    }
    
    if (hasGroups) {
        return (
            <div className="w-full max-w-2xl mx-auto">
                {sortedGroups.map((group, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg mb-2 overflow-hidden">
                        <button 
                            className="w-full text-left p-2 bg-gray-100 hover:bg-gray-200 font-semibold text-md flex items-center gap-2" 
                            onClick={() =>
                              setOpenIndices(prev =>
                                prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
                              )
                            }
                        >
                            <span className="transition-transform transform rotate(-90deg)" style={{ transform: openIndices.includes(index) ? "rotate(0deg)" : "rotate(-90deg)" }}>
                                <img src={caretDown} className="w-6" />
                            </span>
                            {group.group_title}
                        </button>
                        <div className={`transition-all duration-300 ${openIndices.includes(index) ? "max-h-screen p-4" : "max-h-0 p-0 overflow-hidden"}`}>
                            {group.items.map((element, idx) => getElementType(element, index))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }
    
    return (
        <div className="w-full max-w-2xl mx-auto">
          {configElements.map((element, index) => getElementType(element, index))}
        </div>
    );
};

export default Accordion;
